import { ExclamationTriangleIcon } from '@radix-ui/react-icons';
import { Callout, Card } from '@radix-ui/themes';
import { addMilliseconds, differenceInMilliseconds, isFuture } from 'date-fns';
import { RadixColor, RX } from 'enums/radix';
import { ReactNode, useEffect, useRef, useState } from 'react';
import useToastsStore from 'stores/layout/toasts';

interface IProps {
  content: ReactNode;
  icon?: ReactNode;
  color?: RadixColor;
  msDuration?: number;
}

export const CommonToast = (props: IProps) => {
  const { delayUntil, setDelayUntil } = useToastsStore();

  // show the toast when this date is past
  const [showDate] = useState(() => {
    return isFuture(delayUntil) ? delayUntil : new Date();
  });

  // hide the toast when this date is past
  const [hideDate] = useState(
    addMilliseconds(showDate, props.msDuration ?? 3_000)
  );

  // update the store so the next toast can be shown after this toast hides
  useEffect(() => {
    console.debug(`Delaying next toast until after ${hideDate.toISOString()}`);
    setDelayUntil(hideDate);
  }, [hideDate]);

  const [show, setShow] = useState(false);

  // show the toast now or schedule to show the toast in the future
  const showTO = useRef<NodeJS.Timeout>(null);

  useEffect(() => {
    if (isFuture(showDate)) {
      const diffMs = differenceInMilliseconds(showDate, new Date());

      console.debug(`Showing toast in ${diffMs}ms`);

      showTO.current = setTimeout(() => {
        setShow(true);
      }, diffMs);
    } else {
      setShow(true);
    }

    return () => {
      if (showTO.current) {
        clearTimeout(showTO.current);
      }
    };
  }, [showDate]);

  // schedule to hide the toast in the future
  const dismissTO = useRef<NodeJS.Timeout>(null);

  useEffect(() => {
    const diffMs = differenceInMilliseconds(hideDate, new Date());

    console.debug(`Hiding toast in ${diffMs}ms`);

    dismissTO.current = setTimeout(() => {
      setShow(false);
    }, diffMs);

    return () => {
      if (dismissTO.current) {
        clearTimeout(dismissTO.current);
      }
    };
  }, [hideDate]);

  if (!show) {
    return null;
  }

  return (
    <Card className="CommonToast" style={{ padding: 0 }}>
      <Callout.Root color={props.color ?? RX.CLR.WARNING}>
        <Callout.Icon onClick={() => setShow(false)}>
          {props.icon ?? <ExclamationTriangleIcon />}
        </Callout.Icon>
        <Callout.Text
          onClick={() => {
            if (dismissTO.current) {
              clearTimeout(dismissTO.current);
            }
          }}
        >
          {props.content}
        </Callout.Text>
      </Callout.Root>
    </Card>
  );
};
